import { useCallback, useState } from 'react';
import {
  ApiError,
  api,
  type ExperimentoDetalhado,
  type ExperimentoNaTela,
  type ListaDeExperimentos,
  type TetoDeProcedencia,
} from '../lib/api';
import { useResource } from '../lib/useResource';
import { formatNumber } from '../lib/dataState';
import { formatInstant } from '../lib/format';

/**
 * Experimentos pagos: desenhar, acompanhar e abandonar.
 *
 * Fonte: Paid Media Module SPEC v1.0 §26 (Experiments) e Agent SPECS All v1.1
 * §7 (a procedencia do aprendizado).
 *
 * Tres propriedades que esta tela existe para manter:
 *
 * 1. **a recusa do desenho aparece INTEIRA, cada motivo no campo dele.** O
 *    servidor devolve uma lista de recusas com o campo do §26 em falta, e
 *    mostrar so a primeira transformaria o preenchimento num jogo de
 *    adivinhacao de seis rodadas. Quem esta desenhando desiste antes da
 *    terceira;
 *
 * 2. **resultado e FORCA sao duas colunas.** O resultado diz quem venceu; a
 *    forca diz o quanto se pode confiar nisso. Uma vitoria com forca de
 *    hipotese fraca e uma vitoria que NAO autoriza mudar nada, e um rotulo so
 *    apagaria exatamente essa diferenca. Pelo mesmo motivo, amostra
 *    insuficiente nao usa a cor de erro: "ainda nao da para dizer" nao e
 *    derrota, e pintar de vermelho mataria a hipotese pelo silencio dos dados;
 *
 * 3. **a tabela de procedencias fica na tela.** E ela que explica por que uma
 *    conclusao forte virou hipotese fraca sem nenhum numero ter piorado: o teto
 *    vem do TIPO de dado. Sem a tabela, o rebaixamento parece defeito do
 *    sistema, e o proximo passo de quem opera seria pedir para "consertar".
 *
 * Nenhuma lista fechada mora aqui. Comparacoes permitidas, campos exigidos e
 * tetos de procedencia chegam na resposta do servidor: duas copias da mesma
 * lista divergem na segunda semana, e a que envelhece e sempre a do formulario.
 */

// ===========================================================================
// O que a API devolve
// ===========================================================================

/** Uma recusa de desenho, do §26. `field` null quer dizer "nao e de um campo". */
type RecusaDeDesenho = { code: string; field: string | null; reason: string };

/**
 * As recusas que vieram dentro do 409.
 *
 * Le do corpo CRU do erro em vez de refazer a requisicao: o corpo ja viajou
 * junto do `ApiError`. Corpo em outro formato devolve lista vazia, e ai a tela
 * mostra a mensagem geral do servidor, nunca um silencio.
 */
function recusasDoErro(erro: unknown): RecusaDeDesenho[] {
  if (!(erro instanceof ApiError)) return [];
  if (typeof erro.body !== 'object' || erro.body === null) return [];
  const lista = (erro.body as { refusals?: unknown }).refusals;
  if (!Array.isArray(lista)) return [];
  return lista
    .filter((r): r is Record<string, unknown> => typeof r === 'object' && r !== null)
    .map((r) => ({
      code: typeof r.code === 'string' ? r.code : 'RECUSA_SEM_CODIGO',
      field: typeof r.field === 'string' ? r.field : null,
      reason: typeof r.reason === 'string' ? r.reason : 'O servidor recusou sem dizer o motivo.',
    }));
}

// ===========================================================================
// Rotulos em portugues
// ===========================================================================

const ROTULO_DO_ESTADO: Record<string, string> = {
  designed: 'desenhado',
  running: 'em andamento',
  concluded: 'concluído',
  returned_not_isolating: 'devolvido: não isolou a variável',
  abandoned: 'abandonado',
};

/** O que cada resultado do §26 AFIRMA, em uma frase. */
const RESULTADO: Record<string, { rotulo: string; frase: string }> = {
  WIN: {
    rotulo: 'a variante venceu',
    frase: 'O braço testado foi melhor na métrica primária.',
  },
  LOSE: {
    rotulo: 'a variante perdeu',
    frase: 'O braço testado foi pior na métrica primária.',
  },
  NO_MEANINGFUL_DIFFERENCE: {
    rotulo: 'empate',
    frase: 'Houve amostra para comparar, e a diferença entre os braços não importa.',
  },
  INSUFFICIENT_SAMPLE: {
    rotulo: 'ainda não dá para dizer',
    frase:
      'Não houve amostra suficiente para afirmar nada. Isto não é derrota: a hipótese continua de pé, e nada foi testado ainda.',
  },
};

const FORCA: Record<string, { rotulo: string; frase: string; autoriza: boolean }> = {
  strong: {
    rotulo: 'forte',
    frase: 'A evidência sustenta a conclusão.',
    autoriza: true,
  },
  supported: {
    rotulo: 'sustentada',
    frase: 'A evidência sustenta a conclusão, apoiada em estimativa.',
    autoriza: true,
  },
  weak_hypothesis: {
    rotulo: 'hipótese fraca',
    frase: 'Serve como hipótese para o próximo teste, e não autoriza mudar nada.',
    autoriza: false,
  },
  confounded: {
    rotulo: 'confundida',
    frase:
      'Outra variável mudou junto, então não dá para atribuir o resultado ao que foi testado. Não autoriza mudar nada.',
    autoriza: false,
  },
  inconclusive: {
    rotulo: 'inconclusiva',
    frase: 'A evidência não sustenta conclusão nenhuma. Não autoriza mudar nada.',
    autoriza: false,
  },
};

const PROCEDENCIA: Record<string, string> = {
  NATIVE_PROVIDER: 'o número veio do provedor: gasto, impressões, cliques',
  DIRECTLY_OBSERVED: 'nós medimos, no nosso próprio dado',
  ESTIMATED_COHORT: 'veio de coorte, modelo ou proporção, ou seja, é estimativa',
  INFERRED_ATTRIBUTION:
    'alguém ligou um efeito a uma causa sem observar o elo: é correlação vestida de número',
};

/** Os seis campos do §26 na grafia do documento, com o rótulo que a pessoa lê. */
const ROTULO_DO_CAMPO_EXIGIDO: Record<string, string> = {
  hypothesis: 'hipótese',
  'primary metric': 'métrica primária',
  'secondary metrics': 'métricas secundárias',
  'minimum observation window': 'janela mínima de observação',
  'stop rule': 'regra de parada',
  'budget ceiling': 'teto de orçamento',
};

// ===========================================================================
// A tela
// ===========================================================================

export function Experiments() {
  const [versao, setVersao] = useState(0);
  const recarregar = useCallback(() => setVersao((v) => v + 1), []);
  const recurso = useResource<ListaDeExperimentos>(() => api.experimentos(), [versao]);
  const [aberto, setAberto] = useState<string | null>(null);

  if (recurso.kind === 'loading') {
    return (
      <div className="text-sm" aria-busy="true">
        <h1 className="text-2xl font-bold">Experimentos pagos</h1>
        <p className="mt-2 text-ink-muted">Carregando…</p>
      </div>
    );
  }

  if (recurso.kind === 'error') {
    return (
      <div className="max-w-3xl space-y-4 text-sm">
        <h1 className="text-2xl font-bold">Experimentos pagos</h1>
        <div className="mt-4 rounded border border-danger-border bg-danger-surface p-4">
          <p className="font-medium">Não foi possível carregar os experimentos.</p>
          <p className="mt-1">{recurso.message}</p>
          <p className="mt-2 text-xs text-ink-faint">Código: {recurso.code}</p>
        </div>
      </div>
    );
  }

  const dados = recurso.data;

  return (
    <div className="space-y-4 text-sm">
      <header>
        <h1 className="text-2xl font-bold">Experimentos pagos</h1>
        <p className="mt-1 max-w-3xl text-ink-muted">
          Cada experimento compara dois braços que já existem como campanhas. Desenhar não gasta
          nada: quem gasta é ativar cada campanha, que passa pelo portão como qualquer outra.
        </p>
      </header>

      <Procedencias tetos={dados.provenance_ceilings} />

      <Desenhar
        comparacoesPermitidas={dados.allowed_comparisons}
        camposExigidos={dados.required_fields}
        aoCriar={recarregar}
      />

      <Lista
        experimentos={dados.experiments}
        aberto={aberto}
        aoAbrir={(id) => setAberto((atual) => (atual === id ? null : id))}
        versao={versao}
        aoMudar={recarregar}
      />
    </div>
  );
}

// ===========================================================================
// A tabela de procedencias (§7)
// ===========================================================================

function Procedencias({ tetos }: { tetos: TetoDeProcedencia[] }) {
  return (
    <section className="mt-8" aria-labelledby="procedencias">
      <h2
        id="procedencias"
        className="text-xs font-semibold uppercase tracking-wider text-ink-muted"
      >
        De onde vem o número decide até onde a conclusão pode chegar
      </h2>
      <p className="mt-1 max-w-3xl text-xs text-ink-muted">
        Uma conclusão nunca é mais firme do que a pior evidência que a sustenta. É por isso que uma
        conclusão forte aparece rebaixada sem nenhum número ter piorado: o teto abaixo vem do TIPO
        de dado, e não da qualidade da medição.
      </p>

      {tetos.length === 0 ? (
        <p className="mt-3 rounded border border-warn-border bg-warn-surface p-3">
          O servidor não informou nenhuma procedência. Sem esta tabela não dá para explicar um
          rebaixamento de força, então trate qualquer força mostrada abaixo como não explicada.
        </p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[32rem] border-collapse text-left">
            <thead>
              <tr className="border-b border-line">
                <th className="py-1 pr-3 font-medium">Procedência</th>
                <th className="py-1 pr-3 font-medium">O que ela é</th>
                <th className="py-1 font-medium">Força máxima que sustenta</th>
              </tr>
            </thead>
            <tbody data-testid="tabela-de-procedencias">
              {tetos.map((t) => (
                <tr key={t.provenance} className="border-b border-line align-top">
                  <td className="py-1 pr-3 font-medium">{t.provenance}</td>
                  <td className="py-1 pr-3 text-ink-muted">
                    {PROCEDENCIA[t.provenance] ?? 'procedência sem descrição no painel'}
                  </td>
                  <td className="py-1">
                    {FORCA[t.strength_ceiling]?.rotulo ?? t.strength_ceiling}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ===========================================================================
// Desenhar um experimento (§26)
// ===========================================================================

type Formulario = {
  name: string;
  comparison: string;
  hypothesis: string;
  assigned_objective: string;
  primary_metric: string;
  secondary_metrics: string;
  minimum_observation_hours: string;
  stop_rule: string;
  budget_ceiling_minor: string;
  minimum_sample_size: string;
  changed_variables: string;
  arm_a_campaign_id: string;
  arm_b_campaign_id: string;
};

const FORMULARIO_VAZIO: Formulario = {
  name: '',
  comparison: '',
  hypothesis: '',
  assigned_objective: '',
  primary_metric: '',
  secondary_metrics: '',
  minimum_observation_hours: '',
  stop_rule: '',
  budget_ceiling_minor: '',
  minimum_sample_size: '',
  changed_variables: '',
  arm_a_campaign_id: '',
  arm_b_campaign_id: '',
};

/** Lista digitada com vírgula vira lista de verdade, sem item vazio. */
function listaDigitada(texto: string): string[] {
  return texto
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

/**
 * Número digitado vira número, e texto vazio vira zero.
 *
 * Zero e não `null`: o servidor recusa zero com o motivo do §26 escrito por
 * extenso, e é esse motivo que quem está desenhando precisa ler. Mandar `null`
 * cairia na recusa genérica de formato, que não diz qual campo faltou.
 */
function numeroDigitado(texto: string): number {
  const n = Number(texto.trim());
  return Number.isFinite(n) ? Math.trunc(n) : 0;
}

function Desenhar({
  comparacoesPermitidas,
  camposExigidos,
  aoCriar,
}: {
  comparacoesPermitidas: string[];
  camposExigidos: string[];
  aoCriar: () => void;
}) {
  const [form, setForm] = useState<Formulario>(FORMULARIO_VAZIO);
  const [recusas, setRecusas] = useState<RecusaDeDesenho[]>([]);
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [criado, setCriado] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  function mudar(campo: keyof Formulario, valor: string) {
    setForm((atual) => ({ ...atual, [campo]: valor }));
  }

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    setRecusas([]);
    setErroGeral(null);
    setCriado(null);
    setOcupado(true);
    try {
      const resposta = await api.experimentoCriar({
        name: form.name.trim(),
        comparison: form.comparison,
        hypothesis: form.hypothesis.trim(),
        assigned_objective: form.assigned_objective.trim(),
        primary_metric: form.primary_metric.trim(),
        secondary_metrics: listaDigitada(form.secondary_metrics),
        minimum_observation_hours: numeroDigitado(form.minimum_observation_hours),
        stop_rule: form.stop_rule.trim(),
        budget_ceiling_minor: numeroDigitado(form.budget_ceiling_minor),
        minimum_sample_size: numeroDigitado(form.minimum_sample_size),
        changed_variables: listaDigitada(form.changed_variables),
        arm_a_campaign_id: form.arm_a_campaign_id.trim(),
        arm_b_campaign_id: form.arm_b_campaign_id.trim(),
      });
      setForm(FORMULARIO_VAZIO);
      setCriado(resposta.experiment_id);
      aoCriar();
    } catch (erro) {
      /*
        TODAS as recusas, cada uma no campo dela. Guardar so a primeira devolveria
        quem desenha ao regime de descobrir um impedimento por tentativa.
      */
      const lista = recusasDoErro(erro);
      setRecusas(lista);
      setErroGeral(
        erro instanceof ApiError ? erro.message : 'Não foi possível falar com a API do painel.',
      );
    } finally {
      setOcupado(false);
    }
  }

  const semCampo = recusas.filter((r) => r.field === null);

  return (
    <section className="mt-8" aria-labelledby="desenhar">
      <h2 id="desenhar" className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
        Desenhar um experimento
      </h2>
      <p className="mt-1 max-w-3xl text-xs text-ink-muted">
        Desenhar não cria campanha e não gasta. Os dois braços apontam para campanhas que já
        existem, e cada uma continua sendo ativada pelo caminho normal de mídia paga.
      </p>

      {camposExigidos.length > 0 ? (
        <p className="mt-2 max-w-3xl text-xs text-ink-muted">
          Obrigatórios: {camposExigidos.map((c) => ROTULO_DO_CAMPO_EXIGIDO[c] ?? c).join(', ')}. A
          lista vem do servidor, e não do painel.
        </p>
      ) : null}

      {criado === null ? null : (
        <p className="mt-3 rounded-[11px] border border-line bg-surface-sunken p-3">
          Experimento criado e em andamento. Identificador: <strong>{criado}</strong>. Nada começou
          a gastar: ativar cada braço continua sendo um ato separado.
        </p>
      )}

      {erroGeral === null ? null : (
        <div
          className="mt-3 rounded border border-danger-border bg-danger-surface p-3"
          data-testid="recusa-geral"
        >
          <p className="font-medium">{erroGeral}</p>
          {semCampo.length === 0 ? null : (
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {semCampo.map((r) => (
                <li key={r.code}>{r.reason}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <form className="mt-4 max-w-3xl space-y-4" onSubmit={enviar}>
        <Campo
          id="exp-nome"
          rotulo="Nome do experimento"
          ajuda="Como este teste vai ser chamado nas conversas sobre ele."
          valor={form.name}
          aoMudar={(v) => mudar('name', v)}
          recusas={recusas}
          campoDoServidor={null}
        />

        <div>
          <label htmlFor="exp-comparacao" className="block text-xs text-ink-muted">
            O que está sendo comparado
          </label>
          <select
            id="exp-comparacao"
            value={form.comparison}
            onChange={(e) => mudar('comparison', e.target.value)}
            className="mt-1 w-full rounded border border-line px-2 py-1 text-sm"
          >
            <option value="">escolha uma comparação</option>
            {comparacoesPermitidas.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-ink-faint">
            A lista vem do servidor. A variável isolada é consequência da comparação escolhida, e
            por isso não é digitada: digitar as duas permitiria testar uma coisa e declarar outra.
          </p>
        </div>

        <Campo
          id="exp-hipotese"
          rotulo="Hipótese"
          ajuda="O que você espera que aconteça, e que este teste pode derrubar."
          valor={form.hypothesis}
          aoMudar={(v) => mudar('hypothesis', v)}
          recusas={recusas}
          campoDoServidor="hypothesis"
        />
        <Campo
          id="exp-objetivo"
          rotulo="Objetivo atribuído"
          ajuda="O objetivo da campanha. É ele que decide qual métrica pode julgar o resultado."
          valor={form.assigned_objective}
          aoMudar={(v) => mudar('assigned_objective', v)}
          recusas={recusas}
          campoDoServidor={null}
        />
        <Campo
          id="exp-metrica-primaria"
          rotulo="Métrica primária"
          ajuda="A que decide quem venceu."
          valor={form.primary_metric}
          aoMudar={(v) => mudar('primary_metric', v)}
          recusas={recusas}
          campoDoServidor="primary metric"
        />
        <Campo
          id="exp-metricas-secundarias"
          rotulo="Métricas secundárias"
          ajuda="Separadas por vírgula. São elas que mostram o custo de vencer: um braço pode ganhar na primária e piorar o resto."
          valor={form.secondary_metrics}
          aoMudar={(v) => mudar('secondary_metrics', v)}
          recusas={recusas}
          campoDoServidor="secondary metrics"
        />
        <Campo
          id="exp-janela"
          rotulo="Janela mínima de observação, em horas"
          ajuda="Antes disso, olhar o resultado é ler ruído da entrega inicial como desempenho."
          valor={form.minimum_observation_hours}
          aoMudar={(v) => mudar('minimum_observation_hours', v)}
          recusas={recusas}
          campoDoServidor="minimum observation window"
        />
        <Campo
          id="exp-regra-de-parada"
          rotulo="Regra de parada"
          ajuda="Quando este experimento termina. Sem ela, ele roda até alguém lembrar, gastando o teto inteiro."
          valor={form.stop_rule}
          aoMudar={(v) => mudar('stop_rule', v)}
          recusas={recusas}
          campoDoServidor="stop rule"
        />
        <Campo
          id="exp-teto"
          rotulo="Teto de gasto do experimento, em centavos"
          ajuda="Em centavos porque é assim que o servidor recebe, e a tela não converte dinheiro: cada conversão a mais é uma chance a mais de errar por cem vezes. 10000 é cem reais."
          valor={form.budget_ceiling_minor}
          aoMudar={(v) => mudar('budget_ceiling_minor', v)}
          recusas={recusas}
          campoDoServidor="budget ceiling"
        />
        <Campo
          id="exp-amostra"
          rotulo="Amostra mínima por braço"
          ajuda="Abaixo disso o veredito continua sendo que ainda não dá para dizer."
          valor={form.minimum_sample_size}
          aoMudar={(v) => mudar('minimum_sample_size', v)}
          recusas={recusas}
          campoDoServidor={null}
        />
        <Campo
          id="exp-variaveis"
          rotulo="Tudo que muda entre o braço A e o braço B"
          ajuda="Separado por vírgula. Se mudar mais de uma coisa, o experimento não isola nada e o servidor recusa."
          valor={form.changed_variables}
          aoMudar={(v) => mudar('changed_variables', v)}
          recusas={recusas}
          campoDoServidor={null}
        />

        <div className="flex flex-wrap gap-3">
          <Campo
            id="exp-braco-a"
            rotulo="Campanha do braço A"
            ajuda="Identificador da campanha que já existe."
            valor={form.arm_a_campaign_id}
            aoMudar={(v) => mudar('arm_a_campaign_id', v)}
            recusas={recusas}
            campoDoServidor={null}
          />
          <Campo
            id="exp-braco-b"
            rotulo="Campanha do braço B"
            ajuda="Precisa ser outra campanha: comparar uma campanha com ela mesma sempre dá empate."
            valor={form.arm_b_campaign_id}
            aoMudar={(v) => mudar('arm_b_campaign_id', v)}
            recusas={recusas}
            campoDoServidor={null}
          />
        </div>

        <button
          type="submit"
          disabled={ocupado}
          className="rounded border border-accent-border bg-accent-strong px-3 py-1 text-sm text-white disabled:opacity-50"
        >
          Desenhar experimento
        </button>
      </form>
    </section>
  );
}

/**
 * Um campo do formulário, com a recusa DELE embaixo.
 *
 * O erro fica ao lado do campo que o causou, e não numa pilha no topo: com seis
 * campos obrigatórios, uma pilha de motivos sem endereço obriga quem lê a
 * adivinhar qual linha fala de qual caixa.
 */
function Campo({
  id,
  rotulo,
  ajuda,
  valor,
  aoMudar,
  recusas,
  campoDoServidor,
}: {
  id: string;
  rotulo: string;
  ajuda: string;
  valor: string;
  aoMudar: (v: string) => void;
  recusas: RecusaDeDesenho[];
  campoDoServidor: string | null;
}) {
  const minhas = campoDoServidor === null ? [] : recusas.filter((r) => r.field === campoDoServidor);
  return (
    <div className="grow">
      <label htmlFor={id} className="block text-xs text-ink-muted">
        {rotulo}
      </label>
      <input
        id={id}
        value={valor}
        autoComplete="off"
        onChange={(e) => aoMudar(e.target.value)}
        aria-invalid={minhas.length > 0}
        className={
          minhas.length > 0
            ? 'mt-1 w-full rounded border border-danger-border px-2 py-1 text-sm'
            : 'mt-1 w-full rounded border border-line px-2 py-1 text-sm'
        }
      />
      <p className="mt-1 text-xs text-ink-faint">{ajuda}</p>
      {minhas.map((r) => (
        <p key={r.code} className="mt-1 text-xs text-danger-ink" data-testid={`recusa-${r.field}`}>
          {r.reason}
        </p>
      ))}
    </div>
  );
}

// ===========================================================================
// A lista, com resultado e forca em COLUNAS SEPARADAS
// ===========================================================================

function Lista({
  experimentos,
  aberto,
  aoAbrir,
  versao,
  aoMudar,
}: {
  experimentos: ExperimentoNaTela[];
  aberto: string | null;
  aoAbrir: (id: string) => void;
  versao: number;
  aoMudar: () => void;
}) {
  if (experimentos.length === 0) {
    return (
      <section className="mt-8" aria-labelledby="lista">
        <h2 id="lista" className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
          Experimentos
        </h2>
        <p className="mt-3 rounded-[11px] border border-line bg-surface-sunken p-4 text-ink-muted">
          Nenhum experimento desenhado ainda. Nada está sendo comparado e nada está gastando por
          conta de teste.
        </p>
      </section>
    );
  }

  return (
    <section className="mt-8" aria-labelledby="lista">
      <h2 id="lista" className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
        Experimentos
      </h2>

      <ul className="mt-3 space-y-3" data-testid="lista-de-experimentos">
        {experimentos.map((e) => (
          <li key={e.id} className="rounded-card border border-line bg-card p-4 shadow-card">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="font-medium">{e.name}</span>
              <span className="text-xs">{ROTULO_DO_ESTADO[e.state] ?? e.state}</span>
            </div>

            <p className="mt-1 text-xs text-ink-muted">
              {e.comparison ?? 'sem comparação registrada'} · isola {e.isolated_variable} · braços{' '}
              {e.arm_labels.length === 0 ? 'nenhum' : e.arm_labels.join(' e ')} · desenhado em{' '}
              {formatInstant(e.created_at)}
            </p>

            <Veredito experimento={e} />

            <p className="mt-2 text-xs text-ink-muted">
              Hipótese: {e.hypothesis} · julga por {e.primary_metric} · amostra mínima por braço{' '}
              {formatNumber(e.minimum_sample_size)}
            </p>

            <button
              type="button"
              onClick={() => aoAbrir(e.id)}
              className="mt-2 rounded border border-line-strong px-3 py-1 text-xs hover:bg-surface-raised"
            >
              {aberto === e.id ? 'Fechar detalhe' : 'Abrir detalhe'}
            </button>

            {aberto === e.id ? <Detalhe id={e.id} versao={versao} aoMudar={aoMudar} /> : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * O veredito em DUAS colunas: resultado e força.
 *
 * Elas respondem perguntas diferentes. O resultado diz quem venceu; a força diz
 * se aquilo autoriza mudar alguma coisa. Uma vitória de força fraca é uma
 * vitória que não autoriza nada, e um rótulo único apagaria a diferença
 * exatamente no caso em que ela decide gasto.
 *
 * Amostra insuficiente aparece em tom neutro, e nunca no vermelho de erro: não
 * é derrota, é ausência de conclusão.
 */
function Veredito({ experimento }: { experimento: ExperimentoNaTela }) {
  const r = experimento.result;
  const f = experimento.conclusion_strength;

  if (r === null && f === null) {
    return (
      <p
        className="mt-2 rounded-[11px] border border-line bg-surface-sunken p-3 text-xs"
        data-testid="veredito"
      >
        Sem veredito ainda. Nada foi concluído, e a ausência de resultado não é empate.
      </p>
    );
  }

  const resultado = r === null ? null : RESULTADO[r];
  const forca = f === null ? null : FORCA[f];
  const neutro = r === null || r === 'INSUFFICIENT_SAMPLE';

  return (
    <div
      className={
        neutro
          ? 'mt-2 grid gap-3 rounded-[11px] border border-line bg-surface-sunken p-3 sm:grid-cols-2'
          : 'mt-2 grid gap-3 rounded border border-line p-3 sm:grid-cols-2'
      }
      data-testid="veredito"
    >
      <div>
        <p className="text-xs text-ink-muted">Resultado</p>
        <p className="mt-1 font-medium" data-testid="coluna-resultado">
          {r === null ? 'ainda sem resultado' : (resultado?.rotulo ?? r)}
        </p>
        <p className="mt-1 text-xs text-ink-muted">
          {r === null
            ? 'O gancho de aprendizado ainda não concluiu este experimento.'
            : (resultado?.frase ?? 'Resultado sem descrição no painel.')}
        </p>
      </div>

      <div>
        <p className="text-xs text-ink-muted">Força da conclusão</p>
        <p className="mt-1 font-medium" data-testid="coluna-forca">
          {f === null ? 'ainda sem força' : (forca?.rotulo ?? f)}
        </p>
        <p className="mt-1 text-xs text-ink-muted">
          {f === null
            ? 'Sem conclusão registrada, não há força a declarar.'
            : (forca?.frase ?? 'Força sem descrição no painel.')}
        </p>
        {forca !== undefined && forca !== null && !forca.autoriza ? (
          <p className="mt-1 text-xs font-medium" data-testid="nao-autoriza">
            Não use este resultado para mudar campanha nenhuma.
          </p>
        ) : null}
      </div>

      {experimento.result_reason === null ? null : (
        <p className="text-xs text-ink-muted sm:col-span-2">
          Motivo registrado: {experimento.result_reason}
        </p>
      )}
    </div>
  );
}

// ===========================================================================
// O detalhe: bracos, sinais e o abandono
// ===========================================================================

function Detalhe({ id, versao, aoMudar }: { id: string; versao: number; aoMudar: () => void }) {
  const recurso = useResource<ExperimentoDetalhado>(() => api.experimento(id), [id, versao]);

  if (recurso.kind === 'loading') {
    return (
      <p className="mt-3 text-xs text-ink-muted" aria-busy="true">
        Carregando o detalhe…
      </p>
    );
  }

  if (recurso.kind === 'error') {
    return (
      <div className="mt-3 rounded border border-danger-border bg-danger-surface p-3 text-xs">
        <p className="font-medium">Não foi possível carregar este experimento.</p>
        <p className="mt-1">{recurso.message}</p>
      </div>
    );
  }

  const e = recurso.data;

  return (
    <div className="mt-3 rounded border border-line p-3">
      <h3 className="text-xs font-semibold">Os dois braços</h3>
      <ul className="mt-2 space-y-1 text-xs" data-testid="bracos">
        {e.arms.map((b) => (
          <li key={b.label} className="flex flex-wrap gap-2">
            <span className="w-8 font-medium">{b.label}</span>
            <span className="grow">
              {b.campaign_name ?? 'campanha não encontrada ou não visível'}
              {b.campaign_state === null ? '' : ` · ${b.campaign_state}`}
              {b.budget === null ? '' : ` · ${b.budget.formatado}`}
            </span>
          </li>
        ))}
      </ul>

      <h3 className="mt-3 text-xs font-semibold">Regra de parada e teto</h3>
      <p className="mt-1 text-xs text-ink-muted">
        {e.stop_rule ?? 'sem regra de parada registrada'} ·{' '}
        {e.budget_ceiling === null ? 'sem teto registrado' : e.budget_ceiling.formatado} ·{' '}
        {e.minimum_observation_hours === null
          ? 'sem janela mínima'
          : `janela mínima de ${formatNumber(e.minimum_observation_hours)} h`}
      </p>
      <p className="mt-1 text-xs text-ink-muted">
        Métricas secundárias:{' '}
        {e.secondary_metrics.length === 0 ? 'nenhuma registrada' : e.secondary_metrics.join(', ')} ·
        muda entre os braços:{' '}
        {e.changed_variables.length === 0 ? 'nada declarado' : e.changed_variables.join(', ')}
      </p>

      <h3 className="mt-3 text-xs font-semibold">O que este experimento ensinou</h3>
      {e.signals.length === 0 ? (
        <p className="mt-1 text-xs text-ink-muted">
          Nenhum sinal de aprendizado registrado. Ausência de sinal não é sinal de que nada mudou.
        </p>
      ) : (
        <ul className="mt-1 space-y-2 text-xs" data-testid="sinais">
          {e.signals.map((s) => (
            <li key={s.id} className="rounded border border-line p-2">
              <p>{s.observation}</p>
              <p className="mt-1 text-ink-muted">
                Força: {FORCA[s.strength]?.rotulo ?? s.strength} · amostra{' '}
                {formatNumber(s.sample_size)} · registrado em {formatInstant(s.created_at)}
              </p>
              {s.confounders.length === 0 ? null : (
                <p className="mt-1 text-ink-muted">
                  Pode ser explicado também por: {s.confounders.join(', ')}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {e.state === 'designed' || e.state === 'running' ? (
        <Abandonar id={e.id} aoAbandonar={aoMudar} />
      ) : (
        <p className="mt-3 text-xs text-ink-muted">
          Este experimento está em {ROTULO_DO_ESTADO[e.state] ?? e.state} e não pode ser abandonado.
          Conclusão registrada não se apaga: ela é a evidência de que a comparação aconteceu.
        </p>
      )}
    </div>
  );
}

function Abandonar({ id, aoAbandonar }: { id: string; aoAbandonar: () => void }) {
  const [motivo, setMotivo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function abandonar() {
    setErro(null);
    setOcupado(true);
    try {
      await api.experimentoAbandonar(id, { reason: motivo.trim() });
      setMotivo('');
      aoAbandonar();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não foi possível falar com a API do painel.');
    } finally {
      setOcupado(false);
    }
  }

  const semMotivo = motivo.trim().length < 3;

  return (
    <div className="mt-3 border-t border-line pt-3">
      <h3 className="text-xs font-semibold">Abandonar este experimento</h3>
      <p className="mt-1 text-xs text-ink-muted">
        Abandonar não pausa campanha nenhuma: parar de gastar é outra ação, na área de mídia paga. O
        motivo fica no registro do experimento.
      </p>
      <label htmlFor={`motivo-${id}`} className="mt-2 block text-xs text-ink-muted">
        Motivo
      </label>
      <input
        id={`motivo-${id}`}
        value={motivo}
        autoComplete="off"
        onChange={(ev) => setMotivo(ev.target.value)}
        className="mt-1 w-full max-w-xl rounded border border-line px-2 py-1 text-sm"
      />
      <button
        type="button"
        onClick={abandonar}
        disabled={ocupado || semMotivo}
        className="mt-2 rounded border border-line-strong px-3 py-1 text-xs hover:bg-surface-raised disabled:opacity-50"
      >
        Abandonar
      </button>
      {semMotivo ? (
        <p className="mt-1 text-xs text-ink-faint">Escreva o motivo para poder abandonar.</p>
      ) : null}
      {erro === null ? null : (
        <p className="mt-2 rounded border border-danger-border bg-danger-surface p-2 text-xs">
          {erro}
        </p>
      )}
    </div>
  );
}
